import { apiFetch } from '@/utils'
import type { PaymentMethodType } from '@/types/payment-methods'

// ─── Transaction types ──────────────────────────────────────────────
export type TransactionType = 'membership' | 'entry_pass' | 'order'

// ─── Known backend error codes ──────────────────────────────────────
/**
 * Returned (HTTP 409) when the card's MobiusPay vault is invalid. The card has
 * been flagged update_required on the backend; the customer must re-enter it on
 * the manage-cards page before the charge can be retried.
 */
export const CARD_UPDATE_REQUIRED = 'CARD_UPDATE_REQUIRED'

// ─── Charge error carrying the backend errorCode ────────────────────
export class ChargeError extends Error {
	errorCode?: string
	status?: number
	constructor(message: string, opts?: { errorCode?: string; status?: number }) {
		super(message)
		this.name = 'ChargeError'
		this.errorCode = opts?.errorCode
		this.status = opts?.status
	}
}

/** True when a thrown error is the backend's "card vault invalid, re-enter card" signal. */
export function isCardUpdateRequiredError(error: unknown): error is ChargeError {
	return error instanceof ChargeError && error.errorCode === CARD_UPDATE_REQUIRED
}

// ─── Charge request ─────────────────────────────────────────────────
// Common fields every charge sends, plus type-specific context
export interface ChargeRequest {
	/** What kind of purchase this is */
	transactionType: TransactionType
	/** TOKEN or CARD */
	paymentMethodType: PaymentMethodType
	/** USD amount to charge */
	amount: number
	/** Required for CARD payments — the saved card's ID */
	paymentMethodId?: number
	/** User's Firebase UID */
	userUid?: string
	/** User's wallet address (needed for TOKEN payments + identity) */
	walletAddress?: string
	/** TOKEN-only: EIP-712 signature */
	signature?: string
	/** TOKEN-only: the meta-tx forward request / transfer request object */
	forwardRequest?: any
	/** TOKEN-only: nonce from the contract (orders use this) */
	nonce?: number
	/** Per-attempt idempotency key. Required for CARD to avoid Mobius duplicate-transaction errors on retry. */
	idempotencyKey?: string
	/** Optional discount */
	discountId?: number
	discountCode?: string | null
	discountAmount?: number
	/**
	 * Type-specific context for the backend.
	 * Memberships: membershipTemplateId, customerAddress, userUid, waivers[]
	 * Entry passes: entryPassTemplateId, checkInDate, eventDate
	 * Orders: orderId, total, subtotal, tax_amount, taxBreakdown, items, location
	 */
	context: Record<string, any>
}

// ─── Charge response ────────────────────────────────────────────────
export interface ChargeResponse {
	success: boolean
	txHash?: string
	transactionId?: number
	orderId?: string
	message?: string
	[key: string]: any
}

// ─── The single charge call ─────────────────────────────────────────
export async function chargePayment(req: ChargeRequest): Promise<ChargeResponse> {
	const {
		transactionType,
		paymentMethodType,
		amount,
		paymentMethodId,
		userUid,
		walletAddress,
		signature,
		forwardRequest,
		nonce,
		idempotencyKey,
		discountId,
		discountCode,
		discountAmount,
		context,
	} = req

	const body: Record<string, any> = {
		transactionType,
		paymentMethodType,
		amount,
		...context,
	}

	// Identity
	if (userUid) body.userUid = userUid
	if (walletAddress) {
		body.walletAddress = walletAddress
		body.customerAddress = walletAddress
	}

	// CARD-specific
	if (paymentMethodType === 'CARD' && paymentMethodId != null) {
		body.paymentMethodId = paymentMethodId
	}

	// TOKEN-specific
	if (paymentMethodType === 'TOKEN') {
		if (signature) body.signature = signature
		if (forwardRequest) body.forwardRequest = forwardRequest
		if (nonce != null) body.nonce = nonce
	}

	// Idempotency (CARD path: Mobius rejects duplicate identical bodies on retry)
	if (idempotencyKey) body.idempotencyKey = idempotencyKey

	// Discount
	if (discountId != null) body.discountId = discountId
	if (discountCode != null) body.discountCode = discountCode
	if (discountAmount != null) body.discountAmount = discountAmount

	const res = await apiFetch('/v1/billing/charge', {
		method: 'POST',
		body: JSON.stringify(body),
		withCredentials: true,
	})

	const json = await res.json().catch(() => ({}))

	// Errors may arrive either flat ({ message, errorCode }) or nested
	// ({ error: { message, errorCode } }) — read from whichever is present.
	const errBody = json?.error ?? json
	const errorCode: string | undefined = errBody?.errorCode ?? json?.errorCode
	const errMessage: string | undefined = errBody?.message ?? json?.message

	// HTTP-level failure (includes 409 CARD_UPDATE_REQUIRED)
	if (!res.ok) {
		throw new ChargeError(errMessage || `Payment failed (${res.status})`, {
			errorCode,
			status: res.status,
		})
	}

	// Backend returns 200 with success: false for declined transactions
	if (json.success === false) {
		throw new ChargeError(errMessage || 'Payment was declined', {
			errorCode,
			status: res.status,
		})
	}

	return json as ChargeResponse
}

// ─── Cancel / refund response ──────────────────────────────────────
export interface CancelResponse {
	success: boolean
	refundTransaction: {
		id: number
		userId: string
		amount: string
		type: 'refund'
		paymentMethod: 'TOKEN' | 'CARD'
		currency: string
		status: string
		parentTransactionId: number
		orderId: string
		timestamp: string
	}
	originalTransactionId: number
	cascadedRecords: string[]
	txHash?: string
}

// ─── The admin cancel/refund call ──────────────────────────────────
export async function cancelTransaction(transactionId: number): Promise<CancelResponse> {
	const res = await apiFetch('/v1/billing/cancel', {
		method: 'POST',
		body: JSON.stringify({ transactionId }),
		withCredentials: true,
	})

	const json = await res.json().catch(() => ({}))

	if (!res.ok) {
		throw new Error(json.message || json.title || `Cancel failed (${res.status})`)
	}

	if (json.success === false) {
		throw new Error(json.message || 'Cancellation was declined')
	}

	return (json.data ?? json) as CancelResponse
}
